/* eslint-disable no-console */
class PubSub {
  events = {
    addonSelectionChanged: { name: "addon-selection-changed" },
    addToCart: { name: "add-to-cart" },
    cartReady: { name: "cart-ready", replay: true },
    contentInteracted: { name: "content-interacted" },
    updateDOM: { name: "update-dom" },
    filtersApplied: { name: "filters-applied" },
    userLocationSet: { name: "user-location-change", replay: true },
    storesSorted: { name: "stores-sorted", replay: true },
    preferredStoreSet: { name: "preferred-store-set", replay: true },
  };

  #listeners = new Map();
  #last = new Map();

  /**
   * Subscribes to an event. Events with `replay: true` immediately invoke the
   * callback with the last published payload, if one exists.
   * @param {string|{ name: string, replay?: boolean }} event - Event name or definition
   * @param {Function} callback - Called with the published data
   * @returns {Function} Unsubscribe function
   */
  subscribe(event, callback) {
    const eventName = this.#eventName(event);

    if (this.unknownEvent(eventName)) {
      console.warn(`PubSub: Unknown event "${eventName}"`);
      return () => {};
    }

    if (typeof callback !== "function") {
      console.warn("PubSub: Callback must be a function");
      return () => {};
    }

    if (!this.#listeners.has(eventName)) {
      this.#listeners.set(eventName, new Set());
    }

    const listeners = this.#listeners.get(eventName);

    if (listeners.has(callback)) {
      return () => this.unsubscribe(eventName, callback);
    }

    listeners.add(callback);

    if (this.#shouldReplay(eventName) && this.#last.has(eventName)) {
      this.#notify(callback, this.#last.get(eventName));
    }

    return () => this.unsubscribe(eventName, callback);
  }

  /**
   * Unsubscribes from an event
   * @param {string|{ name: string, replay?: boolean }} event - Event name or definition
   * @param {Function} callback - The function to unsubscribe
   */
  unsubscribe(event, callback) {
    const eventName = this.#eventName(event);

    if (this.unknownEvent(eventName)) {
      console.warn(`PubSub: Unknown event "${eventName}"`);
      return;
    }

    this.#listeners.get(eventName)?.delete(callback);
  }

  /**
   * Publishes data to subscribers
   * @param {string|{ name: string, replay?: boolean }} event - Event name or definition
   * @param {*} data - The data to publish
   */
  publish(event, data = {}) {
    const eventName = this.#eventName(event);

    if (this.unknownEvent(eventName)) {
      console.warn(`PubSub: Unknown event "${eventName}"`);
      return;
    }

    if (this.#shouldReplay(eventName)) {
      this.#last.set(eventName, data);
    }

    // Snapshot: subscribers that replace their element (see `live-region.js`)
    // subscribe mid-publish, and a live Set would notify them with this payload.
    [...(this.#listeners.get(eventName) ?? [])].forEach((callback) => {
      this.#notify(callback, data);
    });
  }

  /**
   * Clears the retained payload for a replay event
   * @param {string|{ name: string, replay?: boolean }} event - Event name or definition
   */
  clear(event) {
    const eventName = this.#eventName(event);

    if (this.unknownEvent(eventName)) {
      console.warn(`PubSub: Unknown event "${eventName}"`);
      return;
    }

    this.#last.delete(eventName);
  }

  /**
   * @param {string} eventName
   * @returns {boolean}
   */
  unknownEvent(eventName) {
    if (typeof eventName !== "string" || eventName.length === 0) {
      return true;
    }

    return !Object.values(this.events).some(({ name }) => name === eventName);
  }

  /**
   * @param {string|{ name: string, replay?: boolean }} event
   * @returns {string|undefined}
   */
  #eventName(event) {
    if (typeof event === "string") {
      return event;
    }

    if (event && typeof event === "object" && typeof event.name === "string") {
      return event.name;
    }

    return undefined;
  }

  /**
   * @param {string} eventName
   * @returns {boolean}
   */
  #shouldReplay(eventName) {
    return Object.values(this.events).some(
      ({ name, replay }) => name === eventName && replay,
    );
  }

  /**
   * @param {Function} callback
   * @param {*} data
   */
  #notify(callback, data) {
    try {
      callback(data);
    } catch (error) {
      console.error("PubSub: Subscriber error", error);
    }
  }
}

WYATT.PubSub = new PubSub();

export default PubSub;
